import { ComponentType } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatConnectedDialog, MatConnectedDialogConfig, MatConnectedDialogRef } from './connected-dialog';
import { MatDialogConfig, PopupService } from './popup.service';
import { SentryService } from './sentry.service';

/** 元件載入器。慣例上就是 `() => import('./x').then(m => m.XComponent)` */
export type DialogLoader<T> = () => Promise<ComponentType<T>>;

/**
 * 需要延遲載入元件的彈窗開啟器。
 *
 * ## 為什麼需要這個服務
 *
 * `providedIn: 'root'` 的服務若直接靜態 import 自己的彈窗元件，會把整棵功能樹
 * 拉進 initial bundle。實測有四條這樣的鏈把整套 Web3 SDK
 * （ethers / viem / TonConnect / AppKit）帶進匿名首頁。
 *
 * 解法是動態 `import()`，但若讓每個服務各自手寫，會有兩個問題：
 *   1. 載入中的狀態每處各刻一次
 *   2. chunk 載入失敗沒人處理
 *
 * 因此把模式收斂到這裡。新增彈窗時一律走 `open()`，不要在服務頂部
 * 靜態 import 彈窗元件。
 *
 * ## 用法
 *
 * 載入器一律定義於 `dialog-loaders.ts`，不要在呼叫端內嵌 `import()`：
 *
 * ```ts
 * import { LoginDialog } from 'src/app/shared/service/dialog-loaders';
 *
 * // 開啟（元件此時才下載）。不需要 ref 就用 void，避免 async 往呼叫端擴散
 * openLogin(): void {
 *   void this.lazyDialog.open(LoginDialog, config);
 * }
 * ```
 *
 * ## 關於閒置預熱
 *
 * 曾有一個 `warm()` 以 `requestIdleCallback` 預抓 chunk，用意是把首次點擊的
 * 等待移出互動路徑。實測 bundle 後移除：`login-dialog` chunk 僅 42 KB
 * （Slow 4G 約 200 ms），預熱省下的時間有限，卻讓每次進站都多抓一次
 * —— 在 CDN `Cache-Control` 仍為 0 的情況下無法攤提，且會被 Lighthouse
 * 計入傳輸量與 TBT。若日後有更大的彈窗需要預熱，再視數據加回。
 */
@Injectable({ providedIn: 'root' })
export class LazyDialogService {
  constructor(
    private dialog: MatConnectedDialog,
    private popup: PopupService,
    private sentryService: SentryService,
  ) {}

  /** 載入失敗的共同處理：回報 Sentry 後回傳 null，不拋錯，避免呼叫端各自 try/catch。 */
  private async load<T>(loader: DialogLoader<T>): Promise<ComponentType<T> | null> {
    try {
      // 同一個 specifier 重複呼叫會命中 ES module registry，不會重複下載
      return await loader();
    } catch (error) {
      // 多半是佈署後舊分頁拿到已失效的 chunk 檔名，或單純網路中斷
      this.sentryService.error('LazyDialogService', 'Failed to load dialog chunk', {
        extra: { cause: error instanceof Error ? error.message : String(error) },
        level: 'warning',
      });
      return null;
    }
  }

  /**
   * 載入元件後開啟彈窗。
   *
   * @param loader 動態 import 的載入器
   * @param config 與 `MatConnectedDialog.open` 相同
   * @returns 彈窗 ref；chunk 載入失敗時回傳 null（不拋錯，避免呼叫端各自 try/catch）
   */
  async open<T, D = unknown, R = unknown>(
    loader: DialogLoader<T>,
    config?: MatConnectedDialogConfig<D>,
  ): Promise<MatConnectedDialogRef<T, R> | null> {
    const component = await this.load(loader);

    return component ? this.dialog.open<T, D, R>(component, config) : null;
  }

  /**
   * 同 {@link open}，但走 {@link PopupService}（`MatDialog` 的子類，額外處理進出場動畫）。
   *
   * 專案有兩套彈窗系統，動態載入的需求兩邊都有，因此在此各開一個入口，
   * 而不是讓呼叫端自行內嵌 `import()` —— 載入失敗的處理與 Sentry 回報才不會各刻一份。
   *
   * @returns 彈窗 ref；chunk 載入失敗時回傳 null
   */
  async openPopup<T, D = any, R = any>(
    loader: DialogLoader<T>,
    config?: MatDialogConfig<D>,
  ): Promise<MatDialogRef<T, R> | null> {
    const component = await this.load(loader);

    return component ? this.popup.open<T, D, R>(component, config) : null;
  }
}
